<template>
  <div
    class="n-text-message"
    :class="{
      'n-text-message--right': tooltipPosition === 'left',
      'n-text-message--left': tooltipPosition === 'right',
    }"
  >
    <n-avatar
      :name="message.name"
      :image-url="message.avatarUrl"
      :enable-tooltip="enableAvatarTooltip"
      size="s"
      v-if="displayAvatar"
      class="n-text-message__avatar"
      :class="{
        'n-text-message__avatar--left': tooltipPosition === 'right',
        'n-text-message__avatar--right': tooltipPosition === 'left',
      }"
    ></n-avatar>
    <n-tooltip :position="tooltipPosition">
      <template #default>
        <span :style="getMessageStyle()" class="msg-text" v-html="formattedMessage" />
      </template>
      <template #message>
        <span>
          {{ getMessageTimestamp(message.timestampUtc) }}
          <n-icon icon="alert" fill="#ff6348" v-if="shouldShowAlertIcon(message)" />
          <n-icon
            icon="tick"
            :fill="isRead(message) ? '#57d272' : '#bdbdbd'"
            v-if="shouldShowTickIcon(message)"
          ></n-icon>
        </span>
      </template>
    </n-tooltip>
  </div>
</template>

<script lang="ts" setup>
import moment from 'moment';
import tinycolor2 from 'tinycolor2';
import { MessageContent } from './messageContent';
import NIcon from '../../brand/icon/icon.vue';
import NAvatar from '../avatar/avatar.vue';
import { Message, TextMessage } from './message';
import { MessageStatus } from './messageStatus';
import { computed, PropType } from 'vue';

const props = defineProps({
  outboundMessageColour: {
    type: String,
    default: '#062b80',
  },
  message: {
    type: Object as PropType<TextMessage>,
    required: true,
  },
  displayAvatar: {
    type: Boolean,
    default: false,
  },
  enableAvatarTooltip: {
    type: Boolean,
    default: false,
  },
  tooltipPosition: {
    type: String as PropType<'right' | 'left'>,
    default: 'right',
  },
});

const formattedMessage = computed(() => {
  return new MessageContent(props.message.text as string).EscapeHtml().ProcessUrls().NewLinesToBreaks().Value;
});

function getMessageTimestamp(timestamp: Date): string {
  return moment(timestamp).format('HH:mm');
}

function isRead(m: Message) {
  return m.status === MessageStatus.Read;
}

function shouldShowTickIcon(m: Message) {
  switch (m.status) {
    case MessageStatus.DeliverySucceeded:
    case MessageStatus.Read:
    case MessageStatus.Received:
    case MessageStatus.Sent:
      return true;
    default:
      return false;
  }
}

function shouldShowAlertIcon(m: Message) {
  return m.status === MessageStatus.DeliveryFailed;
}

function getMessageStyle() {
  let backgroundColour = '#f5f5f5';
  let textColour = '#212121';

  if (props.message.direction === 'outbound') {
    backgroundColour = props.outboundMessageColour;
    textColour = tinycolor2(backgroundColour).isDark() ? '#fff' : '#212121';
  }

  if (props.message.status === MessageStatus.DeliveryFailed) {
    backgroundColour = '#ffdada';
    textColour = '#212121';
  }

  return {
    background: backgroundColour,
    color: textColour,
  };
}
</script>

<style lang="scss" scoped>
.n-text-message {
  max-width: 75%;
  width: max-content;
  word-wrap: break-word;
  display: flex;
  align-items: center;

  &--right {
    flex-direction: row-reverse;
    padding-right: 0.5em;
  }

  &--left {
    flex-direction: row;
    padding-left: 0.5em;
  }

  &__avatar {
    flex: 0 0 auto;
    &--left {
      margin-right: 0.5rem;
    }
    &--right {
      margin-left: 0.5rem;
    }
  }
}
.msg-text {
  border-radius: 0.2em;
  padding: 0.75em 1em;
  overflow: hidden;

  &.msg-error {
    background: #ffdada;
  }
}

:deep(.n-tooltip__trigger) {
  display: flex;
}
</style>
